import {
  addOrUpdateDigitalQuestionScore,
  addOrUpdateDigitalSectionScore,
  addOrUpdateDigitalSurveyScore,
} from '../../redux/actions';
import {
  selectDigitalGTGroups,
  selectDigitalQuestionsBySection,
  selectDigitalQuestionScores,
  selectDigitalSectionScores,
  selectDigitalSurveyScores,
  selectGroundTruthsPerSectionAndGroup,
  selectSelectedGroundTruthGroup,
  selectSelectedSection,
} from '../../redux/reducers/SurveyDataReducer';
import {
  DigitalGroundTruth,
  DigitalQuestionScore,
  DigitalSection,
  DigitalSectionScore,
  PartialBy,
  RootState,
} from '../../redux/types';
import { useAppDispatch, useAppSelector } from '../reduxHooks';
import { evaluateGroundTruthGroupScore } from '../../components/utilities/DigitalGroundTruthUtil';

/**
 * Update the following max scores
 * DigitalSurveyScore
 * DigitalSectionScore
 * DigitalQuestionScore
 */
const useUpdateGtAndMaxScores = () => {
  const survey = useAppSelector((state: RootState) => state.SurveyData.survey);
  const selectedSection = useAppSelector(selectSelectedSection);
  const digitalSurveyScores = useAppSelector(selectDigitalSurveyScores);
  const digitalSectionScores = useAppSelector(selectDigitalSectionScores);
  const digitalQuestionScores = useAppSelector(selectDigitalQuestionScores);
  const selectedGroundTruthGroup = useAppSelector(
    selectSelectedGroundTruthGroup,
  );
  const groundTruthsPerSectionAndGroup = useAppSelector(
    selectGroundTruthsPerSectionAndGroup,
  );
  const sectionsById = useAppSelector(
    (state: RootState) => state.SurveyData.digitalSections.byId,
  );
  const questionsPerSection = useAppSelector(selectDigitalQuestionsBySection);
  const digitalGroundTruthGroups = useAppSelector(selectDigitalGTGroups);
  const dispatch = useAppDispatch();

  const updateQuestionMaxPassingScore = (
    newGt: PartialBy<DigitalGroundTruth, 'id'>,
    remove = false,
  ) => {
    if (
      !selectedGroundTruthGroup
      || !digitalQuestionScores
      || !selectedSection?.id
      || !survey?.id
    ) {
      return;
    }
    // undefined if fetch request to server is not completed
    const savedScoresByQId = digitalQuestionScores
      && digitalQuestionScores
        .filter(
          (o) => o.digitalGroundTruthGroupId === selectedGroundTruthGroup?.id
            && o.digitalSectionId === selectedSection?.id,
        )
        .reduce((acc, cur) => {
          acc[cur.digitalQuestionId] = cur;
          return acc;
        }, {} as Record<number, DigitalQuestionScore>);

    const gtByQuestions = questionsPerSection.reduce((acc, cur) => {
      acc[cur.id] = [];
      return acc;
    }, {} as Record<number, DigitalGroundTruth[]>);
    groundTruthsPerSectionAndGroup.forEach((gt) => gtByQuestions[gt.digitalQuestionId]?.push(gt));
    // filter out the old ground truth and push the new one
    gtByQuestions[newGt.digitalQuestionId] = gtByQuestions[
      newGt.digitalQuestionId
    ].filter((gt) => gt.id !== newGt.id);
    if (!remove) {
      gtByQuestions[newGt.digitalQuestionId].push(newGt as DigitalGroundTruth);
    }
    const maxScoreByQuestions: Record<number, number> = {};
    questionsPerSection.forEach((q) => {
      maxScoreByQuestions[q.id] = evaluateGroundTruthGroupScore(
        gtByQuestions[q.id],
      );
    });
    if (!savedScoresByQId) {
      return;
    }
    Object.entries(maxScoreByQuestions).forEach(([qId, score]) => {
      if (savedScoresByQId[Number(qId)]?.maxScore !== score) {
        if (savedScoresByQId[Number(qId)]) {
          dispatch(
            addOrUpdateDigitalQuestionScore({
              ...savedScoresByQId[Number(qId)],
              maxScore: score,
            }),
          );
        } else {
          dispatch(
            addOrUpdateDigitalQuestionScore({
              digitalGroundTruthGroupId: selectedGroundTruthGroup.id,
              digitalSurveyId: survey.id,
              digitalSectionId: selectedSection.id,
              digitalQuestionId: Number(qId),
              maxScore: score,
              passScore: 0,
            }),
          );
        }
      }
    });
  };

  const updateSectionMaxPassingScore = (
    newGt: PartialBy<DigitalGroundTruth, 'id'>,
    remove = false,
  ): Omit<DigitalSectionScore, 'id'> | undefined => {
    if (
      !selectedGroundTruthGroup
      || !selectedSection?.id
      || !digitalSectionScores
      || !survey?.id
    ) {
      return undefined;
    }
    /**
     * gets the current section's DigitalSectionScore object
     * per GroundTruthGroup saved in the database
     */
    const savedScore = digitalSectionScores
      && digitalSectionScores.find(
        (dss) => dss.digitalGroundTruthGroupId === selectedGroundTruthGroup?.id
          && dss.digitalSectionId === selectedSection?.id
          && dss.digitalSurveyId === survey?.id,
      );
    const updatedGroundTruths = groundTruthsPerSectionAndGroup.filter(
      (gt) => gt.id !== newGt.id,
    );
    if (!remove) {
      updatedGroundTruths.push(newGt as DigitalGroundTruth);
    }
    const currentScore = evaluateGroundTruthGroupScore(updatedGroundTruths);
    let payload;
    if (!savedScore) {
      payload = {
        digitalGroundTruthGroupId: selectedGroundTruthGroup.id,
        digitalSectionId: selectedSection.id,
        digitalSurveyId: survey.id,
        maxScore: currentScore,
        passScore: 0,
      };
    } else if (savedScore.maxScore !== currentScore) {
      payload = {
        ...savedScore,
        maxScore: currentScore,
      };
    }
    if (payload) {
      dispatch(addOrUpdateDigitalSectionScore(payload));
    }
    return payload;
  };

  const updateSurveyMaxPassingScore = (
    newSectionScore: Omit<DigitalSectionScore, 'id'>,
  ) => {
    if (
      !digitalSurveyScores
      || !digitalSectionScores
      || !selectedGroundTruthGroup
      || !survey?.id
    ) {
      return;
    }
    const updatedSectionScores = digitalSectionScores.filter(
      (secScore) => !(
        secScore.digitalGroundTruthGroupId
            === newSectionScore.digitalGroundTruthGroupId
          && secScore.digitalSurveyId === newSectionScore.digitalSurveyId
          && secScore.digitalSectionId === newSectionScore.digitalSectionId
      ),
    );
    updatedSectionScores.push(newSectionScore as DigitalSectionScore);
    const allSectionScorePerSelectedGroup = updatedSectionScores.filter(
      (dss) => sectionsById[dss.digitalSectionId] !== undefined
        && dss.digitalGroundTruthGroupId === selectedGroundTruthGroup.id
        // filter out optional sections from overall survey max score
        && !sectionsById[dss.digitalSectionId].optional,
    );
    const totalScorePerGroup = allSectionScorePerSelectedGroup.reduce(
      (acc, cur) => acc + cur.maxScore,
      0,
    );
    const existingSurveyScore = digitalSurveyScores.find(
      (dss) => dss.digitalGroundTruthGroupId === selectedGroundTruthGroup.id,
    );
    if (!existingSurveyScore) {
      dispatch(
        addOrUpdateDigitalSurveyScore({
          digitalGroundTruthGroupId: selectedGroundTruthGroup.id,
          digitalSurveyId: survey.id,
          maxScore: totalScorePerGroup,
          passScore: 0,
        }),
      );
    } else if (
      existingSurveyScore
      && existingSurveyScore.maxScore !== totalScorePerGroup
    ) {
      dispatch(
        addOrUpdateDigitalSurveyScore({
          ...existingSurveyScore,
          maxScore: totalScorePerGroup,
        }),
      );
    }
  };

  /** should only be called on the event when the ground truth is added */
  const updateMaxScoresOnAddGT = (gt: PartialBy<DigitalGroundTruth, 'id'>) => {
    updateQuestionMaxPassingScore(gt);
    const newSectionScore = updateSectionMaxPassingScore(gt);
    if (newSectionScore) {
      updateSurveyMaxPassingScore(newSectionScore);
    }
  };

  const updateMaxScoresOnDeleteGT = (gtId: number) => {
    // update question score
    const deletedGt = groundTruthsPerSectionAndGroup.find(
      (gt) => gt.id === gtId,
    );
    if (!deletedGt) {
      return;
    }
    updateQuestionMaxPassingScore(deletedGt, true);
    const newSecScore = updateSectionMaxPassingScore(deletedGt, true);
    if (newSecScore) {
      updateSurveyMaxPassingScore(newSecScore);
    }
  };

  const recalculateMaxSurveyScoreOnOptionalChange = (
    updatedSection: DigitalSection,
  ) => {
    if (
      !digitalSurveyScores
      || !digitalSectionScores
      || !digitalGroundTruthGroups
      || !survey?.id
    ) {
      return;
    }
    const updatedSectionsById = {
      ...sectionsById,
      [updatedSection.id]: updatedSection,
    };
    // evaluate total survey score per ground-truth-group from the section scores
    digitalGroundTruthGroups.forEach((selectedGt) => {
      const allSectionScorePerSelectedGroup = digitalSectionScores.filter(
        (dss) => updatedSectionsById[dss.digitalSectionId] !== undefined
          && dss.digitalGroundTruthGroupId === selectedGt.id
          // filter out optional sections from overall survey max score
          && !updatedSectionsById[dss.digitalSectionId].optional,
      );
      const totalScorePerGroup = allSectionScorePerSelectedGroup.reduce(
        (acc, cur) => acc + cur.maxScore,
        0,
      );
      const existingSurveyScore = digitalSurveyScores.find(
        (dss) => dss.digitalGroundTruthGroupId === selectedGt.id,
      );
      if (
        existingSurveyScore
        && existingSurveyScore.maxScore !== totalScorePerGroup
      ) {
        dispatch(
          addOrUpdateDigitalSurveyScore({
            ...existingSurveyScore,
            maxScore: totalScorePerGroup,
          }),
        );
      }
    });
  };

  return {
    updateMaxScoresOnAddGT,
    updateMaxScoresOnDeleteGT,
    recalculateMaxSurveyScoreOnOptionalChange,
  };
};

export default useUpdateGtAndMaxScores;
